import PropTypes from 'prop-types'

import classNames from 'classnames'
import { useState } from 'react'

// libs
import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'
import { viewSharedProps } from '@aytm/app/stats/page/question-views/vk/lib/constants'
import {
  generateInfographicWestendorpModel,
  generateInfographicPurchaseProbabilityModel,
  generateInfographicPurchaseFrequencyModel,
  generateInfographicProfitRevenueModel
} from '@aytm/app/stats/page/question-views/vk/lib/model'
import { useTamSection } from '@aytm/app/stats/page/question-views/vk/components/views/infographic/lib/hooks'
import { calcTableProps, getPointName } from '@aytm/app/stats/page/question-views/vk/lib/utils'

import { getTutorialVideoById } from '@aytm/app/stats/page/components/resources/tutorial-videos'

// components
import { Slider } from 'primereact/slider'
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'

import Table from '@aytm/app/stats/components/data-views/table/components/Table'
import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'

import Text, { PlainText } from '@aytm/app/stats/page/question-views/vk/components/text/Text'
import Chart from '@aytm/app/stats/page/question-views/vk/components/chart/Chart'
import ScrollAnchor from '@aytm/app/stats/page/question-views/vk/components/views/infographic/components/scroll-anchor/ScrollAnchor'

import TutorialVideo from '@aytm/lib/rfd-ui/components/advanced/tutorial-video'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// styles
import style from '@aytm/app/stats/page/question-views/vk/components/views/infographic/styles/Infographic.module.scss'
import { NO_PRINT } from '@aytm/app/stats/page/question-views/components/lib/constants'

const Infographic = ({
  id,
  text,
  components,
  period,
  fqPeriod,
  defaults,
  palette,
  globalChartMode,
  config,
  answer,
  navTo,
  isLoading,
  isAnimated,
  onModelChange,
  onExport
}) => {
  const [localNavTo, setLocalNavTo] = useState(navTo)
  const { periodArray = [], product, fqPeriodsList = [], periodsList = [] } = defaults
  let sectionIndex = 0
  // Sequential headless-export index per rendered sub-chart. `captureIndex++` sits
  // inside each chart's conditional, so short-circuiting keeps indices gap-free.
  let captureIndex = 0

  const westendorpModel = generateInfographicWestendorpModel({ id, answer, period })
  const purchaceProbabilityModel = generateInfographicPurchaseProbabilityModel({ id, answer, period })
  const purchaseFrequencyModel = generateInfographicPurchaseFrequencyModel({ id, answer, fqPeriod })
  const profitRevenueModel = generateInfographicProfitRevenueModel({
    id,
    answer,
    period,
    chartMode: globalChartMode,
    product
  })

  const tableProps = calcTableProps({
    answer,
    period,
    isLoading,
    clickable: product !== 1 && isComponentOn(components, 'edit_controls'),
    filterRows: ['all', 'infographic'],
    onClick: position => {
      setLocalNavTo(getPointName({ position, answer, period }))
    }
  })

  const handleCloseClick = () => {
    onModelChange({
      infographic: false
    })
  }

  const onSelectChange = event => {
    onModelChange({
      [event.target.name]: String(event.value)
    })
  }

  const { tamValue, tamText, tamMaxValue, onSlideChange, onSlideEnd, handleInputChange, handleKeyPressChange } =
    useTamSection({
      answer,
      config,
      onModelChange
    })

  return (
    <div className={style.infographic}>
      <div className={classNames(style.detailed, style.headerButtons)}>
        {answer?.omitVW ? null : (
          <TutorialVideo
            className={style.tutorialButton}
            icon="icon-aytmfd-videotut-icons"
            videos={getTutorialVideoById('vk')}
            videoId="vk"
          />
        )}
        <span className={style.closeButton} aria-label="Close infographic" {...pressable(handleCloseClick)}>
          <i className="icon-aytmfd-x" />
        </span>
      </div>
      {/* Product Description */}
      {!answer?.omitVW && (
        <div className={classNames(style.detailed, style.detailedShadow)}>
          {text && (
            <div className={style.content}>
              <div className={style.title}>PRODUCT DESCRIPTION:</div>
              <Text value={text} className={style.text} />
            </div>
          )}

          <div className={style.content}>
            <span data-scroll={`${id}-vw_optimal`} />
            <span data-scroll={`${id}-vw_toocheap`} />
            <span data-scroll={`${id}-vw_tooexp`} />
            <span data-scroll={`${id}-vw_indiff`} />
            <div className={style.title}>{++sectionIndex}. VAN WESTENDORP PRICE SENSITIVITY TEST:</div>
            <Text value={answer?.texts?.VWText} className={style.text} />
            {answer?.acceptable_range_VW?.[0] && (
              <div className={style.prices}>
                <span className={style.price}>
                  <span className={style.priceHead}>{answer?.VW_big_text?.[0]}</span>
                  {answer?.VW_small_text?.[0]}
                </span>
                <span className={style.priceRange}>{answer?.acceptable_range_VW?.[0]}</span>
              </div>
            )}
          </div>
          <ChartPlaceholder required isLoading={isLoading} isInverse isAnimated={isAnimated}>
            <Chart
              className={style.chartWestendorp}
              id={id}
              model={westendorpModel}
              palette={palette}
              captureIndex={captureIndex++}
              infographic
              isWestendorp
              onExport={onExport}
            />
          </ChartPlaceholder>
        </div>
      )}
      {/* Purchase Probability Section */}
      <div className={classNames(style.detailed, style.detailedShadow)}>
        <div className={style.content}>
          <span data-scroll={`${id}-max_prob`} />
          <div className={style.title}>{++sectionIndex}. PURCHASE PROBABILITY:</div>
          <Text value={answer?.texts?.probText} className={style.text} />
          <PlainText value={answer?.acceptable_range_prob} className={classNames(style.text, style.textRight)} />
        </div>
        <ChartPlaceholder required isLoading={isLoading} isAnimated={isAnimated} isInverse>
          <Chart
            id={id}
            model={purchaceProbabilityModel}
            palette={palette}
            captureIndex={captureIndex++}
            infographic
            isWestendorp={false}
            onExport={onExport}
          />
        </ChartPlaceholder>
      </div>
      {/* TAM Section */}
      <div className={classNames(style.detailed, style.detailedShadow)}>
        <div className={style.content}>
          <div className={style.title}>{++sectionIndex}. TOTAL ADDRESSABLE MARKET (TAM)</div>
          <Text value={answer?.texts?.totAddText} className={style.text} />
          <div className={style.tamSlider}>
            <Slider
              // the library's aria-valuenow is the handle's offset in percent, so the TAM itself rides here
              data-value={tamValue}
              aria-label="Total addressable market"
              min={100}
              max={tamMaxValue}
              value={tamValue}
              labelTemplate={value => `${value}`}
              onChange={onSlideChange}
              onSlideEnd={onSlideEnd}
            />
            <InputText
              className={style.tamInput}
              aria-label="Total addressable market"
              value={tamText ?? tamValue}
              name="tam"
              onChange={handleInputChange}
              onKeyPress={handleKeyPressChange}
            />
          </div>
          {parseInt(answer?.TAM?.[0], 10) !== parseInt(answer?.subTAM?.[0], 10) && (
            <div className={style.tamContent}>
              <div className={style.tamContentNote}>
                <b>NOTE:</b> SOME FILTERS APPLIED, REDUCING TAM TO{' '}
                <b>{parseFloat((answer?.reducedBy?.[0] * 100).toFixed(2))}</b>%
              </div>
              <div className={style.tamContentSub}>
                SUB TAM: <b>{answer?.subTAM_formatted?.[0]}</b>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Purchase Frequency Section */}
      {![2, 5].includes(product) && (
        <div className={classNames(style.detailed, style.detailedShadow)}>
          <div className={style.content}>
            <span data-scroll={`${id}-max_freq`} />
            <div className={style.title}>{++sectionIndex}. PURCHASE FREQUENCY</div>
            <Text value={answer?.texts?.freqText?.[`p${fqPeriod}`]} className={style.text} />
            <PlainText className={classNames(style.text, style.textRight)} value={answer?.acceptable_range_freq} />
            <div className={`${style.dropdownPanel} ${NO_PRINT}`}>
              <span className={style.dropdownTitle}>Timeframe/purchase frequency:</span>
              <Dropdown
                className={style.dropdown}
                aria-label="Timeframe/purchase frequency"
                ariaLabel="Timeframe/purchase frequency"
                name="fq_period"
                value={fqPeriod}
                options={fqPeriodsList}
                optionValue="value"
                optionLabel="text"
                optionDisabled="disabled"
                fixedDropdownDir="bottom"
                onChange={onSelectChange}
              />
            </div>
          </div>
          <ChartPlaceholder required isLoading={isLoading} isAnimated={isAnimated} isInverse>
            <Chart
              id={id}
              model={purchaseFrequencyModel}
              palette={palette}
              captureIndex={captureIndex++}
              infographic
              isWestendorp={false}
              onExport={onExport}
            />
          </ChartPlaceholder>
        </div>
      )}
      {/* Profit and Revenue Section */}
      <div className={classNames(style.detailed, style.detailedShadow)}>
        <div className={style.content}>
          <span data-scroll={`${id}-max_rev`} />
          <span data-scroll={`${id}-max_prof`} />
          <div className={style.title}>
            {++sectionIndex}. MAXIMUM {[2, 4].includes(product) && 'REVENUE '}
            {[5, 6].includes(product) && 'REVENUE & PROFIT '}
            {[4, 6].includes(product) && <>IN {periodArray?.find(p => p.value === period)?.text}</>}
          </div>
          <Text className={style.text} value={answer?.texts?.revProfText?.[`p${period}`]} />
          <div className={style.profitRevenueGrid}>
            {[2, 4, 5, 6].includes(product) && answer?.revprof_rev_big_text?.[`p${period}`]?.[0] && (
              <div className={style.price}>
                <span className={style.priceHead}>{answer?.revprof_rev_big_text?.[`p${period}`]?.[0]}</span>
                {answer?.revprof_rev_small_text?.[`p${period}`]?.[0]}
              </div>
            )}
            {[5, 6].includes(product) && answer?.revprof_prof_big_text?.[`p${period}`]?.[0] && (
              <div className={style.price}>
                <span className={style.priceHead}>{answer?.revprof_prof_big_text?.[`p${period}`]?.[0]}</span>
                {answer?.revprof_prof_small_text?.[`p${period}`]?.[0]}
              </div>
            )}
          </div>
          {![2, 5].includes(product) && (
            <div className={`${style.dropdownPanel} ${NO_PRINT}`}>
              <span className={style.dropdownTitle}>Timeframe/purchase frequency:</span>
              <Dropdown
                className={style.dropdown}
                aria-label="Timeframe/purchase frequency"
                ariaLabel="Timeframe/purchase frequency"
                name="period"
                value={period}
                options={periodsList}
                optionValue="value"
                optionLabel="text"
                optionDisabled="disabled"
                fixedDropdownDir="bottom"
                onChange={onSelectChange}
              />
            </div>
          )}
        </div>
        <ChartPlaceholder required isLoading={isLoading} isAnimated={isAnimated} isInverse>
          <Chart
            id={id}
            model={profitRevenueModel}
            palette={palette}
            infographic
            isWestendorp={false}
            onExport={onExport}
            captureIndex={captureIndex++}
          />
        </ChartPlaceholder>
      </div>
      {/* Takeaway Points */}
      <div className={classNames(style.detailed)}>
        <div className={style.content}>
          <div className={style.title}>TAKEAWAY POINTS</div>
          <Text className={style.text} value={answer?.texts?.takeaway?.[`p${period}`]} />
        </div>
      </div>
      {Object.hasOwn(answer, 'pointsOfInterest') && !isLoading && (
        <Table cols={tableProps.cols} rows={tableProps.rows} classNames={tableProps.classNames} />
      )}
      {answer?.texts?.disclaimer && (
        <div className={classNames(style.detailed)}>
          <div className={style.content}>
            <div className={style.title}>DISCLAIMER &amp; NEXT STEPS</div>
            <Text className={style.text} value={answer?.texts?.disclaimer} />
          </div>
        </div>
      )}
      <ScrollAnchor id={id} navTo={localNavTo} onModelChange={onModelChange} setLocalNavTo={setLocalNavTo} />
    </div>
  )
}

Infographic.propTypes = {
  ...viewSharedProps,
  navTo: PropTypes.string
}

export default Infographic
